import React from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import PerformanceBadge from './PerformanceBadge';
import ImpactBadge from './ImpactBadge';
import EsrrBadge from './EsrrBadge';
import { OperationItem } from '../../services/types/Operation';
import { Cell } from '../../shared/components/Table';
import DocStatusBadge from './DocStatusBadge';
import { dateStr } from '../../shared/misc/utils';
import DisclosureBadge from './DisclosureBadge';
import RiskBadge from './RiskBadge';
import { Button } from '../../shared/components';
import Pending from './Pending';

const getCells = (
  showRationale: (rationaleType: string, rationale: string) => void,
  showIndicators: (operationId: number) => void
) => {
  const dollarESLocale = Intl.NumberFormat('es');

  const { t } = useTranslation();
  const noData = 'Data not available';

  const cells: Cell<OperationItem>[] = [
    {
      name: 'number',
      value: (x) => x.number,
      render: (x, o) => <Link to={`/operations/${o.number}`}>{x}</Link>,
      column: 'opNumber',
      css: 'S1',
    },
    { name: 'name', value: (x) => x.name, column: 'operationName', css: 'S3' },
    {
      name: 'esgStatus',
      column: 'status',
      value: (x) => x.esgStatus,
      render: (x) => <Pending textInfo={x} />,
    },
    {
      name: 'stage',
      value: (x) => x.stage,
      render: (x) => <Pending textInfo={x} />,
    },
    {
      name: 'phase',
      value: (x) => x.phase,
      render: (x) => <Pending textInfo={x} />,
    },
    { name: 'operationYear', value: (x) => x.operationYear },
    { name: 'lendingType', value: (x) => x.lendingType },
    { name: 'typeModality', column: 'type_modality', value: (x) => x.typeModality },
    {
      name: 'isReformulated',
      value: (x) => (x.stage !== noData ? x.isReformulated : ''),
      render: (x, obj) => <Pending textInfo={obj.isReformulated !== null ? x : ''} />,
    },
    { name: 'sectorSubSector', value: (x) => x.sectorSubSector },
    { name: 'regionCountry', value: (x) => x.regionCountry },
    { name: 'subSectorName', value: (x) => x.subSectorName },
    { name: 'executingAgency', value: (x) => x.executingAgency },
    { name: 'borrower', value: (x) => x.borrower },
    {
      name: 'idbAmount',
      value: (x) => dollarESLocale.format(x.idbAmount),
      render: (x, obj) => <Pending textInfo={obj.idbAmount !== null ? x : ''} />,
    },
    {
      name: 'totalProjectCost',
      value: (x) => dollarESLocale.format(x.totalProjectCost),
      render: (x, obj) => <Pending textInfo={obj.totalProjectCost !== null ? x : ''} />,
    },
    {
      name: 'percDisburstment',
      value: (x) => (x.stage !== noData ? x.percDisburstment : ''),
      render: (x, obj) => <Pending textInfo={obj.percDisburstment !== null ? x : ''} />,
    },
    {
      name: 'esic',
      value: (x) => x.esic,
      render: (x) => <ImpactBadge text={x} />,
    },
    {
      name: 'esicDate',
      value: (x) => dateStr(x.esicDate),
    },
    { name: 'espsPolicies', value: (x) => x.espsPolicies, css: 'S3' },
    {
      name: 'dccrc',
      value: (x) => x.dccrc,
      render: (x) => <RiskBadge text={x} />,
    },
    {
      name: 'esrr',
      value: (x) => x.esrr,
      render: (x) => (x === noData ? <Pending textInfo={x} /> : <EsrrBadge text={x} />),
    },
    {
      name: 'esrrDate',
      value: (x) => dateStr(x.esrrDate),
    },
    {
      name: 'esrrRationale',
      value: (x) => x.esrrRationale,
      sortDisabled: true,
      render: (v, obj) => (
        <Button
          htmlId={`esrr_button_${obj.id}`}
          className="btn-tertiary no-padding"
          icon="clipboard-list"
          text=""
          onClick={() => showRationale('esrrRationale', v)}
          title={t('rationaleDetails')}
        />
      ),
    },
    {
      name: 'previousEsrr',
      value: (x) => x.previousEsrr,
      render: (x) => <EsrrBadge text={x} />,
      sortDisabled: true,
    },
    {
      name: 'previousEsrrDate',
      value: (x) => dateStr(x.previousEsrrDate),
      sortDisabled: true,
      dependsOn: 'previousEsrr',
    },
    {
      name: 'previousEsrrRationale',
      value: (x) => x.previousEsrrRationale,
      sortDisabled: true,
      dependsOn: 'previousEsrr',
      render: (v, obj) => (
        <Button
          htmlId={`prev_esrr_button_${obj.id}`}
          className="btn-tertiary no-padding"
          icon="clipboard-list"
          text=""
          onClick={() => showRationale('previousEsrrRationale', v)}
          title={t('rationaleDetails')}
        />
      ),
    },
    {
      name: 'ghgEmissionsOperation',
      value: (x) => x.ghgEmissionsOperation,
      render: (x) => <Pending textInfo={x} />,
      alternativeText: 'ghgInformation',
    },
    {
      name: 'ghgEmissionsConstruction',
      value: (x) => x.ghgEmissionsConstruction,
      render: (x) => <Pending textInfo={x} />,
      dependsOn: 'ghgEmissionsOperation',
    },
    {
      name: 'esgIndicatorsApply',
      column: 'esgIndicators',
      value: (x) => x.esgIndicatorsApply,
      sortDisabled: true,
      render: (apply, obj) =>
        obj.esgIndicatorsApply !== 0 ? (
          <Button
            htmlId={`esg_indicators_button_${obj.id}`}
            className="btn-tertiary no-padding"
            icon="clipboard-list"
            text=""
            onClick={() => showIndicators(obj.id)}
            title={t('indicatorsDetails')}
          />
        ) : (
          <></>
        ),
    },
    {
      name: 'spr',
      value: (x) => x.spr,
      render: (x) => <PerformanceBadge code={x ? x.substr(1) : ''} />,
    },
    {
      name: 'sprDate',
      value: (x) => dateStr(x.sprDate),
    },
    {
      name: 'sprRationale',
      value: (x) => x.sprRationale,
      sortDisabled: true,
      render: (v, obj) => (
        <Button
          htmlId={`spr_button_${obj.id}`}
          className="btn-tertiary no-padding"
          icon="clipboard-list"
          text=""
          onClick={() => showRationale('sprRationale', v)}
          title={t('rationaleDetails')}
        />
      ),
    },
    {
      name: 'prevSpr',
      value: (x) => x.prevSpr,
      render: (x) => <PerformanceBadge code={x ? x.substr(1) : ''} />,
      sortDisabled: true,
    },
    {
      name: 'prevSprDate',
      value: (x) => dateStr(x.prevSprDate),
      sortDisabled: true,
      dependsOn: 'prevSpr',
    },
    {
      name: 'prevSprRationale',
      value: (x) => x.prevSprRationale,
      sortDisabled: true,
      dependsOn: 'prevSpr',
      render: (v, obj) => (
        <Button
          htmlId={`prev_spr_button_${obj.id}`}
          className="btn-tertiary no-padding"
          icon="clipboard-list"
          text=""
          onClick={() => showRationale('prevSprRationale', v)}
          title={t('rationaleDetails')}
        />
      ),
    },
    {
      name: 'groupName',
      column: 'esgGroup',
      value: (x) => x.groupName,
      css: 'S1',
      sortDisabled: true,
    },
    {
      name: 'primaryMember',
      value: (x) => x.primaryMember,
      sortDisabled: true,
      dependsOn: 'esgMembers',
    },
    {
      name: 'esgMembers',
      value: (x) => x.esgMembers,
      css: 'S3',
      sortDisabled: true,
    },
    {
      name: 'teamLeader',
      value: (x) => x.teamLeader,
      css: 'S3',
      sortDisabled: true,
    },
    {
      name: 'analysisMissionDate',
      value: (x) => (x.stage !== noData ? dateStr(x.analysisMissionDate) : ''),
      render: (x, obj) => <Pending textInfo={obj.percDisburstment !== null ? x : ''} />,
      css: 'S1',
    },
    {
      name: 'assessmentsDisclosure',
      value: (x) => x.assessmentsDisclosure,
      render: (x, obj) =>
        x !== noData ? (
          <div className="disclosure-wrapper">
            <DisclosureBadge value={x.split(',')[0]} label="ESD1" />
            <DisclosureBadge value={x.split(',')[1]} label="ESD2" />
          </div>
        ) : (
          <Pending textInfo={obj.assessmentsDisclosure !== null ? x : ''} />
        ),
      column: 'assessmentsDisclosure',
      sortDisabled: true,
    },
    {
      name: 'ermDate',
      value: (x) => (x.stage !== noData ? dateStr(x.ermDate) : ''),
      render: (x, obj) => <Pending textInfo={obj.ermDate !== null ? x : ''} />,
      css: 'S1',
    },
    {
      name: 'qrrDate',
      value: (x) => (x.stage !== noData ? dateStr(x.qrrDate) : ''),
      render: (x, obj) => <Pending textInfo={obj.qrrDate !== null ? x : ''} />,
      css: 'S1',
    },
    {
      name: 'approvalDate',
      value: (x) => (x.stage !== noData ? dateStr(x.approvalDate) : ''),
      render: (x, obj) => <Pending textInfo={obj.approvalDate !== null ? x : ''} />,
    },
    {
      name: 'esgDocumentStatus',
      column: 'documentStatus',
      value: (x) => x.esgDocumentStatus,
      render: (x) => (
        <div className="disclosure-wrapper flex-column">
          {x
            .split(',')
            .filter((y) => !!y)
            .map((y) => (
              <DocStatusBadge code={y} />
            ))}
        </div>
      ),
    },
    {
      name: 'esgIndicators',
      column: 'EsgIndicators',
      value: (x) => x.esgIndicators || '',
    },
    {
      name: 'kickOffDate',
      column: 'kickOffMeetingDate',
      value: (x) => (x.stage !== noData ? dateStr(x.kickOffDate) : ''),
      render: (x, obj) => <Pending textInfo={obj.stage !== null ? x : ''} />,
      css: 'S1',
    },
    {
      name: 'nextCommittee',
      value: (x) => (x.stage !== noData ? x.nextCommittee : ''),
      render: (x, obj) => <Pending textInfo={obj.nextCommittee !== null ? x : ''} />,
      css: 'S1',
    },
    {
      name: 'nextMission',
      value: (x) => (x.stage !== noData ? x.nextMission : ''),
      render: (x, obj) => <Pending textInfo={obj.nextMission !== null ? x : ''} />,
    },
    {
      name: 'lastMission',
      value: (x) => (x.stage !== noData ? x.lastMission : ''),
      render: (x, obj) => <Pending textInfo={obj.lastMission !== null ? x : ''} />,
    },
    {
      name: 'pmr',
      value: (x) => x.pmr,
      render: (x) => <Pending textInfo={x} />,
    },
    {
      name: 'm95DisbursedDate',
      column: 'm95Disbursed',
      value: (x) => (x.stage !== noData ? dateStr(x.m95DisbursedDate) : ''),
      render: (x) => <Pending textInfo={x} />,
      css: 'S1',
    },
    {
      name: 'closingDate',
      value: (x) => (x.stage !== noData ? dateStr(x.closingDate) : ''),
      render: (x, obj) => <Pending textInfo={obj.closingDate !== null ? x : ''} />,
      css: 'S1',
    },
    {
      name: 'miciCases',
      value: (x) => x.miciCases,
      render: (x, obj) => <Pending textInfo={obj.miciCases !== null ? x : ''} />,
      css: 'S3',
      sortDisabled: true,
    },
    {
      name: 'coFinancing',
      column: 'co-Financing',
      value: (x) => x.coFinancing,
      css: 'S3',
    },
    {
      name: 'frameworkApproach',
      column: 'frameworkApproach',
      value: (x) => x.frameworkApproach,
      css: 'S3',
    },
  ];

  return cells.filter((c) => c.name && (!c.disable || !c.disable()));
};

export default getCells;
